import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart,
  faDog,
  faHandHoldingHeart,
  faUsers,
  faFileLines,
  faSquareCheck,
} from "@fortawesome/free-solid-svg-icons";
import FaqAccordion, { FaqItem } from "../components/FaqAccordion";

const FOSTER_GUIDE_URL =
  "https://pup-profile-prod.s3.us-west-2.amazonaws.com/resources/Foster+Guide.pdf";

const GetInvolved: React.FC = () => {
  const adoptSteps = [
    {
      title: "Browse dogs",
      description:
        "Explore adoptable dogs and learn about their personalities, needs, and adoption requirements.",
    },
    {
      title: "Submit application",
      description:
        "Apply through the rescue partner listed on the dog's profile. For urgent shelter dogs, contact the shelter directly to confirm availability.",
    },
    {
      title: "Meet your match",
      description:
        "Connect with the rescue or shelter, ask questions, and make sure it's the right fit for both you and the dog.",
    },
    {
      title: "Welcome them home",
      description:
        "Complete the adoption process and start life with your new best friend.",
    },
  ];

  const adoptionFaq: FaqItem[] = [
    {
      question: "Can I adopt a dog directly through Pup Profile?",
      answer: (
        <p>
          No, Pup Profile provides a platform for Community Partners to showcase
          pups in need of a forever home, but does not have physical custody of
          any dogs. To adopt an At-Risk dog who is still at the shelter, call
          ahead and go to the shelter to adopt. Shelter info is listed on each
          dog's profile. To adopt a dog In Rescue/Foster Care, contact the
          rescue or apply via their website, listed on each dog's profile.
        </p>
      ),
    },
    {
      question:
        "I see a dog that I think I want to adopt. How do I know they are still available?",
      answer: (
        <p>
          To confirm availability of At-Risk dogs, call and email the shelter.
          To confirm availability of dogs In Rescue/Foster Care, contact the
          rescue via their website. Shelter contact info and rescue websites are
          listed on each dog's profile.
        </p>
      ),
    },
    {
      question: "What is the “Adopted” page in the Browse Dogs section?",
      answer: (
        <p>
          We love a happy ending! Those are dogs that have found their forever
          home after being posted on Pup Profile.
        </p>
      ),
    },
    {
      question: "I adopted a dog I found on Pup Profile!",
      answer: (
        <p>
          Yay! We love to hear it. If you would like your dog and adoption story
          to be featured on our social media, send us a message via the{" "}
          <a href="/contact" className="font-semibold text-blue-strong underline">
            Contact Us
          </a>{" "}
          page or on Instagram: @pupprofile.
        </p>
      ),
    },
  ];

  const fosterSteps = [
    {
      title: "Apply",
      description:
        "Fill out a foster interest form for an urgent dog from their profile, or complete our general foster interest form.",
    },
    {
      title: "Get matched",
      description:
        "We'll review your application and connect you with a dog that matches your experience, household, and availability.",
    },
    {
      title: "Provide care",
      description:
        "Welcome your foster dog home and give them a safe place to decompress and settle into a daily routine while they wait for adoption. Patience and consistency are key during this adjustment period.",
    },
    {
      title: "Help find their forever home",
      description:
        "Share updates, photos, and information about your foster dog to help potential adopters get to know them.",
    },
  ];

  const partnerBenefits = [
    "Create and manage dog profiles",
    "Receive foster interest forms directly for at-risk dogs",
    "Access a toolkit of helpful resources",
    "Connect with other Community Partners",
    "Reach more adopters with free Google Ads promotion",
  ];

  const resourceGuides = [
    { title: "Foster Guide", url: FOSTER_GUIDE_URL },
    {
      title: "Behavioral Foster Guide",
      url: "https://pup-profile-prod.s3.us-west-2.amazonaws.com/resources/Behavioral+Foster+Guide.pdf",
    },
    {
      title: "Medical Foster Guide",
      url: "https://pup-profile-prod.s3.us-west-2.amazonaws.com/resources/Medical+Foster+Guide.pdf",
    },
    {
      title: "Training Guide: Your first 100 days with your Rescue Dog",
      url: "https://pup-profile-prod.s3.us-west-2.amazonaws.com/resources/Training+Guide.pdf",
    },
  ];

  const fosterFaq: FaqItem[] = [
    {
      question: "How does fostering work with Pup Profile?",
      answer: (
        <p>
          When you submit a Foster Interest Form through Pup Profile, it will be
          reviewed by our Community Partners who are working to save at-risk
          shelter dogs. They will match you with a reputable rescue organization
          that can save the dog and will fit your needs. Once that connection is
          made, the rescue will likely have you fill out their own foster
          application. Every rescue is different, and they will guide you through
          their foster process. At that point, the foster's primary point of
          contact is the rescue. It is imperative that fosters communicate any
          questions or concerns directly to the rescue as soon as they come up.
        </p>
      ),
    },
    {
      question: "Where can I apply to foster?",
      answer: (
        <p>
          You can apply to foster a specific dog via their profile, or you can
          fill out our general foster interest form{" "}
          <a
            href="/foster-interest"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-blue-strong underline"
          >
            here
          </a>
          .
        </p>
      ),
    },
    {
      question: "How long will I have my foster dog for?",
      answer: (
        <p>
          The ideal foster will keep a dog until they are adopted, which could
          be a couple weeks or a few months (or longer) depending on the dog and
          how well the foster and rescue are promoting them.
        </p>
      ),
    },
    {
      question: "Is there a minimum time requirement to foster?",
      answer: (
        <p>
          If you are not able to foster for at least a month, you can still help
          by being a temporary foster! Temp fosters are crucial to rescue
          operations, and will take care of a foster's dog when they are
          traveling. To be a temp foster, fill out our foster interest form{" "}
          <a
            href="/foster-interest"
            className="font-semibold text-blue-strong underline"
          >
            here
          </a>
          .
        </p>
      ),
    },
    {
      question: "How can I prepare for my new foster dog?",
      answer: (
        <p>
          See our{" "}
          <a
            href={FOSTER_GUIDE_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-blue-strong underline"
          >
            Foster Guide
          </a>{" "}
          for tips on how to welcome a foster dog into your home.
        </p>
      ),
    },
    {
      question: "Can I foster if I have a dog at home already?",
      answer: (
        <p>
          Many fosters have dogs of their own. However, you must be able and
          willing to separate the dogs at least until your new foster has had
          time to decompress. Separation may need to continue depending on the
          behavior of both dogs.
        </p>
      ),
    },
    {
      question: "Does fostering cost money?",
      answer: (
        <p>
          Fostering is free! Saving a life should never be a financial burden
          for a foster. That said, rescues have various ways of providing food
          and supplies to their fosters. Some provide supplies up front, some
          reimburse for purchased supplies, and some put together Amazon or Chewy
          Wishlists. Before agreeing to foster for a rescue, discuss how they
          handle providing supplies and make sure you are comfortable with their
          protocol.
        </p>
      ),
    },
    {
      question: "Fostering harder than I thought. What should I do?",
      answer: (
        <p>
          Communicate any concerns about your foster pup with your rescue. They
          may have trainers available to assist you, or can provide you with
          advice. Keep in mind that dogs need time to decompress and settle into
          their new environment. Please be patient and give them the space they
          need. Don't give up on them! Check out our{" "}
          <a
            href="/resources"
            className="font-semibold text-blue-strong underline"
          >
            resources page
          </a>{" "}
          for helpful foster guides.
        </p>
      ),
    },
    {
      question:
        "Do I need a yard to be a foster? What if I live in an apartment?",
      answer: (
        <p>
          You absolutely do not need a yard to be a foster, you just need to be
          able to take your pup on walks/bathroom breaks. Apartment life suits
          many dogs very well.
        </p>
      ),
    },
    {
      question:
        "I submitted a Foster Interest Form but I haven't been contacted by a Community Partner. What should I do?",
      answer: (
        <p>
          Please allow our community partners three business days to reach out to
          you about your application. If you haven't heard anything after three
          days, submit an inquiry to Pup Profile via the{" "}
          <a
            href="/contact"
            className="font-semibold text-blue-strong underline"
          >
            Contact Us
          </a>{" "}
          page.
        </p>
      ),
    },
    {
      question:
        "Does applying to foster through Pup Profile guarantee that I will be placed with a dog?",
      answer: (
        <p>
          No, it does not. Our Community Partners prioritize what's best for the
          dogs, and someone else might be a better fit for a certain dog.
          However, fosters are always in demand, so even if you are not matched
          with the first dog you apply for, consider submitting a General Foster
          Interest Form. This will allow our Community Partners to find a dog that
          is a good fit for you.
        </p>
      ),
    },
  ];
  const involvementCards = [
    {
      icon: faHeart,
      title: "Adopt",
      description:
        "Browse adoptable dogs & start the adoption process with our shelter & rescue partners",
      buttonLabel: "Find a pup",
      link: "/browse",
    },
    {
      icon: faDog,
      title: "Foster",
      description:
        "Provide a safe temporary home & help a dog while they wait for adoption",
      buttonLabel: "Become a foster",
      link: "/foster-interest",
    },
    {
      icon: faHandHoldingHeart,
      title: "Donate",
      description:
        "Support rescue efforts, medical care & life-saving work for at-risk dogs",
      buttonLabel: "Donate now",
      link: "/donate",
    },
    {
      icon: faUsers,
      title: "Volunteer",
      description:
        "Are you a rescue, networker, or shelter volunteer? Become a Pup Profile Community Partner!",
      buttonLabel: "Join our team",
      link: "/register",
    },
  ];

  return (
    <section className="mb-11 font-poppins text-gray-dark">
      <section className="flex w-full flex-col items-center justify-center px-6 py-16 lg:px-16 lg:py-28">
        <div className="flex w-full max-w-[900px] flex-col items-center gap-10">
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-col items-center gap-3">
              <span className="w-fit rounded bg-blue-pale px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                Ways to get involved
              </span>
              <h1 className="text-center text-4xl font-extrabold text-black">
                Be the bridge from shelter to safety
              </h1>
            </div>
            <p className="text-center text-xl font-normal text-gray-dark">
              There are LOTS of ways to help shelter pups in need. From opening
              your home to making a donation, your support helps dogs leave
              overcrowded shelters and move toward a brighter future. Let&rsquo;s
              all do our part!
            </p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/browse"
                className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
              >
                Find a pup
              </Link>
              <Link
                to="/foster-interest"
                className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
              >
                Become a foster
              </Link>
            </div>
            <Link
              to="/login"
              className="flex items-center gap-1 text-sm font-semibold text-blue-strong underline transition hover:text-blue-dark"
            >
              I&rsquo;m a rescue partner
              <svg
                className="size-4"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M11.5 4.5v5a.75.75 0 0 1-1.5 0V6.31l-5.22 5.22a.75.75 0 0 1-1.06-1.06L8.94 5.25H5.75a.75.75 0 0 1 0-1.5h5a.75.75 0 0 1 .75.75Z" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* Ways to get involved cards */}
      <section className="flex w-full flex-col items-center px-6 pb-20 lg:px-16">
        <div className="flex flex-wrap items-stretch justify-center gap-10">
          {involvementCards.map((card) => (
            <div
              key={card.title}
              className="flex w-[271px] max-w-full flex-col items-center gap-8 p-6"
            >
              <div className="flex flex-1 flex-col items-center gap-6">
                <FontAwesomeIcon
                  icon={card.icon}
                  className="text-[2.5rem] text-gray1"
                />
                <div className="flex flex-col items-center gap-3">
                  <h3 className="text-2xl font-semibold text-black">
                    {card.title}
                  </h3>
                  <p className="text-center text-base font-normal text-gray-dark">
                    {card.description}
                  </p>
                </div>
              </div>
              <Link
                to={card.link}
                className="flex h-12 w-[223px] max-w-full items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
              >
                {card.buttonLabel}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Adopt article */}
      <section className="flex w-full flex-col gap-12 bg-blue-pale px-6 py-20 lg:px-32">
        {/* Intro */}
        <div className="flex flex-col items-stretch gap-6 lg:flex-row lg:gap-0">
          <div className="min-w-0 w-full lg:flex-1">
            <img
              src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/get-involved/adopt.jpg"
              alt="An adoptable rescue dog"
              className="h-72 w-full rounded-xl object-cover object-top sm:h-96 lg:h-[484px]"
            />
          </div>
          <div className="flex w-full flex-col justify-center gap-6 lg:flex-1 lg:px-16">
            <h2 className="text-3xl font-bold text-blue-strong">Adopt</h2>
            <p className="text-base font-normal text-black">
              Ready to welcome a dog into your family? Browse available dogs,
              find a good match, and follow the adoption steps provided by the
              rescue or shelter. Every dog deserves a loving home, and your new
              best friend might be waiting for you right now.
            </p>
            <Link
              to="/browse"
              className="flex h-12 w-fit min-w-[152px] items-center justify-center rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
            >
              Find your new friend
            </Link>
          </div>
        </div>

        {/* How it works */}
        <div className="flex flex-col gap-6">
          <h3 className="text-2xl font-semibold text-gray-dark">How it works</h3>
          <div className="flex flex-col gap-8 sm:flex-row">
            {adoptSteps.map((step, index) => (
              <div
                key={step.title}
                className="flex flex-col items-center gap-8 rounded-lg bg-blue-strong p-6 sm:min-w-0 sm:flex-1"
              >
                <div className="flex flex-col items-center gap-6">
                  <div className="flex size-10 items-center justify-center rounded-full bg-white text-lg font-bold text-blue-strong">
                    {index + 1}
                  </div>
                  <div className="flex flex-col items-center gap-3">
                    <p className="text-xl font-semibold text-white">
                      {step.title}
                    </p>
                    <p className="text-center text-base font-normal text-blue-pale">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Adoption FAQ */}
        <div className="flex flex-col gap-6">
          <h3 className="text-2xl font-semibold text-gray-dark">
            Adoption FAQ
          </h3>
          <FaqAccordion items={adoptionFaq} />
        </div>
      </section>

      {/* Foster article */}
      <section className="flex w-full flex-col gap-12 px-6 py-20 lg:px-32">
        <div className="flex w-full max-w-[900px] flex-col gap-6">
          <h2 className="text-3xl font-bold text-blue-strong">Foster</h2>
          <p className="text-base font-normal text-black">
            Every foster home creates space for another dog in need. By opening
            your home temporarily, you're giving a shelter dog the chance to
            decompress, feel safe, and prepare for adoption. The commitment is
            temporary, and rescue partners typically cover food, supplies, and
            medical care along the way.
          </p>
        </div>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-stretch lg:gap-0">
          {/* Image */}
          <div className="w-full overflow-hidden rounded-xl lg:flex-1">
            <img
              src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/get-involved/foster.jpg"
              alt="A foster dog enjoying the outdoors"
              className="h-72 w-full object-cover sm:h-96 lg:h-full"
            />
          </div>

          {/* Steps + buttons */}
          <div className="flex w-full flex-col gap-6 lg:flex-1 lg:px-16">
            <h3 className="text-2xl font-semibold text-gray-dark">
              How it works
            </h3>
            <div className="flex flex-col">
              {fosterSteps.map((step, index) => (
                <div key={step.title} className="flex gap-6">
                  <div className="flex flex-col items-center">
                    <div className="size-4 shrink-0 rounded-full bg-blue" />
                    {index < fosterSteps.length - 1 && (
                      <div className="w-px flex-1 bg-blue" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2 pb-8">
                    <p className="text-xl font-semibold text-black">
                      {step.title}
                    </p>
                    <p className="text-sm font-normal text-gray3">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/foster-interest"
                className="flex h-12 min-w-[152px] items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
              >
                Fill out foster interest form
              </Link>
              <a
                href={FOSTER_GUIDE_URL}
                target="_blank"
                rel="noreferrer"
                className="flex h-12 min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
              >
                <FontAwesomeIcon icon={faFileLines} className="text-lg" />
                Foster guide
              </a>
            </div>
          </div>
        </div>

        {/* Foster FAQ */}
        <div className="flex flex-col gap-6">
          <h3 className="text-2xl font-semibold text-gray-dark">Foster FAQ</h3>
          <FaqAccordion items={fosterFaq} />
        </div>
      </section>

      {/* Join our team */}
      <section className="flex w-full flex-col items-stretch gap-10 bg-blue-pale px-6 py-20 lg:flex-row lg:gap-0 lg:px-16">
        {/* Copy */}
        <div className="flex w-full flex-col justify-center gap-10 lg:flex-1 lg:px-16">
          <div className="flex max-w-[900px] flex-col gap-6">
            <div className="flex flex-col gap-3">
              <span className="w-fit rounded bg-orange px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-white">
                For rescues &amp; shelter volunteers
              </span>
              <h2 className="text-3xl font-bold text-blue-strong">
                Join our team
              </h2>
            </div>
            <p className="text-base font-normal text-black">
              <span className="font-semibold">Community Partners</span> are the
              people helping dogs move from shelters into safe homes. Whether
              you're a rescue, networker, or shelter volunteer, Pup Profile gives
              you tools to promote dogs, connect with potential fosters and
              adopters, and manage cases from one dashboard.
            </p>
          </div>
          <div className="flex flex-col gap-6">
            <h3 className="text-2xl font-semibold text-gray-dark">
              As a Community Partner, you'll be able to:
            </h3>
            <ul className="flex flex-col gap-4">
              {partnerBenefits.map((benefit) => (
                <li key={benefit} className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faSquareCheck}
                    className="shrink-0 text-xl text-blue"
                  />
                  <span className="text-base font-normal text-black">
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
            <Link
              to="/register"
              className="flex h-12 w-fit min-w-[152px] items-center justify-center rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
            >
              Sign up
            </Link>
          </div>
        </div>

        {/* Image */}
        <div className="w-full overflow-hidden rounded-xl lg:flex-1">
          <img
            src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/get-involved/join-our-team.jpg"
            alt="Two rescue dogs playing in the grass"
            className="h-72 w-full object-cover sm:h-96 lg:h-full"
          />
        </div>
      </section>

      {/* Resources */}
      <section className="flex w-full flex-col items-center gap-12 px-6 py-20 lg:px-16">
        <div className="flex w-full max-w-[900px] flex-col items-center gap-6">
          <h2 className="text-center text-3xl font-bold text-blue-strong">
            Resources
          </h2>
          <p className="text-center text-base font-normal text-black">
            Fostering or adopting for the first time is a big step! Our
            comprehensive <span className="font-bold">Foster Guides</span> and
            one-page <span className="font-bold">Quick Guides</span> will help
            you get off to the right start with your new pup.
          </p>
        </div>
        <div className="grid w-full max-w-[900px] grid-cols-1 gap-4 sm:grid-cols-2">
          {resourceGuides.map((guide) => (
            <a
              key={guide.title}
              href={guide.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 rounded-lg bg-blue-pale p-4 transition hover:bg-blue-light"
            >
              <FontAwesomeIcon
                icon={faFileLines}
                className="shrink-0 text-2xl text-blue"
              />
              <span className="text-base font-bold text-gray6">
                {guide.title}
              </span>
            </a>
          ))}
        </div>
        <Link
          to="/quick-guides"
          className="flex h-12 min-w-[152px] items-center justify-center gap-2 rounded-md bg-blue px-6 text-base font-bold text-white transition hover:bg-blue-dark"
        >
          More Quick Guides
          <svg
            className="size-6"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M11.5 4.5v5a.75.75 0 0 1-1.5 0V6.31l-5.22 5.22a.75.75 0 0 1-1.06-1.06L8.94 5.25H5.75a.75.75 0 0 1 0-1.5h5a.75.75 0 0 1 .75.75Z" />
          </svg>
        </Link>
      </section>

      {/* Donate to the Pups Fund */}
      <section className="flex w-full flex-col items-stretch bg-blue-pale lg:flex-row lg:justify-between">
        <div className="flex flex-col gap-10 px-6 py-12 lg:w-[650px] lg:py-20 lg:pl-32 lg:pr-20">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <span className="w-fit rounded bg-white px-2 py-1 text-sm font-medium uppercase tracking-[0.42px] text-blue-strong">
                Donate to the pups fund
              </span>
              <h2 className="text-4xl font-extrabold text-black">
                Help fund a dog&rsquo;s second chance
              </h2>
            </div>
            <p className="text-xl font-normal text-gray-dark">
              100% of donations to{" "}
              <span className="font-semibold">The Pups Fund</span> go directly
              toward the care of rescue dogs. From routine medical care to
              emergency treatment, your support helps rescue partners save more
              dogs in need.
            </p>
          </div>
          <Link
            to="/donate"
            className="flex h-12 w-fit min-w-[152px] items-center justify-center gap-2 rounded-md bg-orange px-6 text-base font-bold text-white transition hover:bg-orange-dark"
          >
            <FontAwesomeIcon icon={faHeart} className="text-lg" />
            Donate
          </Link>
        </div>
        <div className="lg:flex-1">
          <img
            src="https://pup-profile-prod.s3.us-west-2.amazonaws.com/get-involved/donate.jpg"
            alt="A happy rescue dog supported by The Pups Fund"
            className="h-64 w-full object-cover sm:h-96 lg:h-full"
          />
        </div>
      </section>
    </section>
  );
};

export default GetInvolved;
